/** Stage E3.1 — Trade Replay page.
 * Route: /replay/:positionId
 * Combines immutable snapshot (what we knew) + historical bars (what happened)
 * + "Would you still take this?" feedback.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Check, X, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ChartPanel from "@/components/ChartPanel";
import WhyPanel from "@/components/WhyPanel";
import { NewsPanel } from "@/components/FundamentalsNewsPanels";
import { formatLocalDateISO } from "@/lib/dates";

const REASONS = [
  { code: "entry_early",       label: "Entry too early" },
  { code: "entry_late",        label: "Entry too late" },
  { code: "stop_tight",        label: "Stop too tight" },
  { code: "stop_wide",         label: "Stop too wide" },
  { code: "target_unrealistic",label: "Target unrealistic" },
  { code: "size_too_large",    label: "Position too large" },
  { code: "wrong_portfolio",   label: "Wrong portfolio" },
  { code: "risk_ignored",      label: "Risk warning ignored" },
  { code: "ai_thesis_weak",    label: "AI thesis weak" },
  { code: "setup_failed",      label: "AI thesis valid but setup failed" },
  { code: "market_shift",      label: "Market conditions changed unexpectedly" },
  { code: "execution_issue",   label: "Execution issue" },
  { code: "other",             label: "Other" },
];

function fmt(n, d = 2) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });
}

function VerdictBadge({ v }) {
  if (!v) return null;
  const cls = /BUY/.test(v) ? "pnl-up" : /SELL|REDUCE/.test(v) ? "pnl-down" :
              /WATCH|HOLD/.test(v) ? "text-signal-yellow" : "";
  return <span className={`chip !text-[10px] border-current ${cls}`}>{v.replace(/_/g, " ")}</span>;
}

export default function TradeReplay() {
  const { positionId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/replay/${positionId}`)
      .then(r => {
        setData(r.data);
        const fb = r.data?.feedback;
        if (fb) { setAnswer(fb.answer); setReasons(fb.reasons || []); setComment(fb.comment || ""); }
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [positionId]);

  const submit = async () => {
    if (!answer) { toast.error("Choose Yes / No / Unsure first"); return; }
    setSubmitting(true);
    try {
      await api.post(`/replay/${positionId}/feedback`, { answer, reasons, comment });
      toast.success("Feedback saved — this becomes House Playbook training data");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="p-8 text-xs text-muted-foreground flex items-center gap-2">
      <Loader2 size={12} className="animate-spin"/>Loading replay…
    </div>
  );
  if (!data) return (
    <div className="p-8 text-sm">
      <button onClick={() => nav(-1)} className="btn-ghost mb-3"><ArrowLeft size={12}/> Back</button>
      <div className="border border-dashed border-border/40 p-4 text-xs text-muted-foreground">
        Replay unavailable for this position — either no snapshot was captured (trade approved before Batch E3.1)
        or the position doesn&apos;t belong to this workspace.
      </div>
    </div>
  );

  const pos = data.position || {};
  const snap = data.snapshot || {};
  const hist = data.history || {};
  const trade = snap.trade || {};
  const analysis = snap.analysis || {};
  const meta = analysis.analysis_meta || {};
  const port = snap.portfolio || {};
  const isClosed = pos.status === "closed";
  const entryPrice = trade.entry || pos.avg_entry;
  const exitPrice = pos.close_price;
  const decisionDate = snap.captured_at;

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <div className="flex items-center gap-3 mb-1">
        <button onClick={() => nav(-1)} className="btn-ghost !px-2"><ArrowLeft size={14}/></button>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl mono">Trade Replay · {pos.symbol}</h1>
            <VerdictBadge v={meta.verdict}/>
            {isClosed ? <span className="chip !text-[10px] border-current text-muted-foreground">CLOSED</span> :
              <span className="chip pnl-up border-current !text-[10px]">OPEN</span>}
          </div>
          <div className="mono text-[11px] text-muted-foreground mt-0.5">
            Approved {formatLocalDateISO(decisionDate)} · Opened {formatLocalDateISO(pos.opened_at || pos.created_at)}
            {pos.closed_at && ` · Closed ${formatLocalDateISO(pos.closed_at)}`}
          </div>
        </div>
        <div className="ml-auto grid grid-cols-2 gap-3 text-right">
          <div>
            <div className="label-cap">Realized P&L</div>
            <div className={`mono text-lg ${(pos.realized_pnl || 0) >= 0 ? "pnl-up" : "pnl-down"}`}>
              ${fmt(pos.realized_pnl)}
            </div>
          </div>
          <div>
            <div className="label-cap">Portfolio</div>
            <div className="mono">{port.name || port.key || "—"}</div>
          </div>
        </div>
      </div>

      <div className="border border-signal-yellow/30 bg-signal-yellow/5 p-3 text-xs">
        <div className="mono uppercase tracking-wider text-signal-yellow mb-1">Hindsight guard</div>
        This replay only surfaces information available on <b>{formatLocalDateISO(decisionDate)}</b>.
        News, chart bars, and any events shaded in yellow are strictly post-decision — they are hindsight, not signals you had.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border/60 p-3 space-y-2">
          <div className="label-cap">Immutable snapshot · as approved</div>
          <div className="grid grid-cols-2 text-xs gap-y-1">
            <div className="text-muted-foreground">Verdict</div><div className="mono text-right">{meta.verdict || "—"}</div>
            <div className="text-muted-foreground">Score</div><div className="mono text-right">{meta.opportunity_score ?? "—"}</div>
            <div className="text-muted-foreground">Risk</div><div className="mono text-right">{meta.risk_score ?? "—"}</div>
            <div className="text-muted-foreground">Confidence</div><div className="mono text-right">{meta.confidence ?? "—"}</div>
            <div className="text-muted-foreground">Entry</div><div className="mono text-right">${fmt(trade.entry)}</div>
            <div className="text-muted-foreground">Stop</div><div className="mono text-right">${fmt(trade.stop)}</div>
            <div className="text-muted-foreground">Target</div><div className="mono text-right">${fmt(trade.target)}</div>
            <div className="text-muted-foreground">Qty</div><div className="mono text-right">{fmt(trade.qty, 0)}</div>
            <div className="text-muted-foreground">Portfolio</div><div className="mono text-right">{port.key || "—"}</div>
            <div className="text-muted-foreground">Portfolio cash then</div><div className="mono text-right">${fmt(port.available_cash)}</div>
          </div>
        </div>
        <div className="border border-border/60 p-3">
          <div className="label-cap mb-2">Excursions during holding</div>
          <div className="grid grid-cols-2 text-xs gap-y-1">
            <div className="text-muted-foreground">Max favourable (MFE)</div>
            <div className="mono text-right pnl-up">{hist.excursions?.mfe_pct != null ? `${hist.excursions.mfe_pct}%` : "—"}</div>
            <div className="text-muted-foreground">Max adverse (MAE)</div>
            <div className="mono text-right pnl-down">{hist.excursions?.mae_pct != null ? `${hist.excursions.mae_pct}%` : "—"}</div>
            <div className="text-muted-foreground">Exit price</div>
            <div className="mono text-right">{exitPrice ? `$${fmt(exitPrice)}` : "—"}</div>
            <div className="text-muted-foreground">Holding bars</div>
            <div className="mono text-right">{(hist.holding || []).length}</div>
          </div>
        </div>
      </div>

      {hist.available ? (
        <ChartPanel
          symbol={pos.symbol}
          entry={trade.entry} stop={trade.stop} target={trade.target}
          showReplay entryDate={pos.opened_at || pos.created_at} exitDate={pos.closed_at}
        />
      ) : (
        <div className="border border-dashed border-border/40 p-4 text-xs text-muted-foreground text-center">
          Historical chart bars unavailable — the stored snapshot above is still the source of truth.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border/60 p-3">
          <div className="label-cap mb-2">Why we took this trade</div>
          <WhyPanel why={analysis.why}/>
        </div>
        <div className="border border-border/60 p-3">
          <NewsPanel symbol={pos.symbol} decisionDate={decisionDate}/>
        </div>
      </div>

      <div className="border border-signal-blue/40 p-4 space-y-3" data-testid="replay-feedback">
        <div className="text-sm font-medium">Knowing only what was available at the time, would you still take this trade?</div>
        <div className="flex items-center gap-2">
          {["yes", "no", "unsure"].map(a => (
            <button
              key={a}
              data-testid={`replay-answer-${a}`}
              onClick={() => setAnswer(a)}
              className={`chip border-current !text-xs !px-3 py-1 ${answer === a
                  ? (a === "yes" ? "pnl-up" : a === "no" ? "pnl-down" : "text-signal-yellow")
                  : "text-muted-foreground hover:text-foreground"}`}
            >
              {a === "yes" ? <Check size={11} className="inline mr-1"/> :
                a === "no" ? <X size={11} className="inline mr-1"/> :
                <HelpCircle size={11} className="inline mr-1"/>}
              {a.charAt(0).toUpperCase() + a.slice(1)}
            </button>
          ))}
        </div>
        {answer === "no" && (
          <div className="space-y-2">
            <div className="label-cap">Why not (optional — becomes House Playbook training data)</div>
            <div className="flex flex-wrap gap-1.5">
              {REASONS.map(r => (
                <button
                  key={r.code}
                  onClick={() => setReasons(prev => prev.includes(r.code) ? prev.filter(x => x !== r.code) : [...prev, r.code])}
                  className={`chip !text-[10px] border-current ${reasons.includes(r.code) ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`replay-reason-${r.code}`}
                >{r.label}</button>
              ))}
            </div>
          </div>
        )}
        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Optional comment…"
          rows={2}
          className="w-full bg-transparent border border-border/40 p-2 text-xs mono resize-y outline-none"
          data-testid="replay-comment"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={submit}
            disabled={submitting || !answer}
            className="chip pnl-up border-current !text-[11px] disabled:opacity-40"
            data-testid="replay-submit"
          >{submitting ? <Loader2 size={11} className="animate-spin inline mr-1"/> : null}Save feedback</button>
          {data.feedback && (
            <span className="mono text-[10px] text-muted-foreground">Last saved {String(data.feedback.updated_at).slice(0, 16)}</span>
          )}
        </div>
      </div>
    </div>
  );
}
